import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  CheckCircle2,
  Circle,
  Clock,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  X,
  ChevronDown,
  Loader2,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { useGetIdentity } from "ra-core";

type TaskType = "call" | "email" | "dm" | "meeting" | "other";

interface Task {
  id: number;
  text: string;
  type: TaskType;
  due_date: string | null;
  done_date: string | null;
  contact_id: number | null;
  sales_id: number | null;
  contact_name?: string;
  contact_company?: string;
}

interface NewTaskForm {
  text: string;
  type: TaskType;
  due_date: string;
  notes: string;
  contact_search: string;
  contact_id: number | null;
  contact_name: string;
}

const TYPE_CONFIG: Record<
  TaskType,
  {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    color: string;
  }
> = {
  call: { icon: Phone, label: "Call", color: "text-blue-400" },
  email: { icon: Mail, label: "Email", color: "text-purple-400" },
  dm: { icon: MessageSquare, label: "DM", color: "text-emerald-400" },
  meeting: { icon: Calendar, label: "Meeting", color: "text-amber-400" },
  other: { icon: Circle, label: "Other", color: "text-slate-400" },
};

const supabase = getSupabaseClient();

const formatDueDate = (dateStr: string | null): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor(
    (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const isOverdue = (dateStr: string | null): boolean => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
};

export const FollowupsPage = () => {
  const { identity } = useGetIdentity();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [filter, setFilter] = useState<"open" | "done" | "all">("open");
  const [contactSuggestions, setContactSuggestions] = useState<
    { id: number; name: string; company: string }[]
  >([]);
  const [form, setForm] = useState<NewTaskForm>({
    text: "",
    type: "call",
    due_date: "",
    notes: "",
    contact_search: "",
    contact_id: null,
    contact_name: "",
  });
  const [saving, setSaving] = useState(false);
  const [completingId, setCompletingId] = useState<number | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("tasks")
        .select(
          "id, text, type, due_date, done_date, contact_id, sales_id, contacts(first_name, last_name, companies(name))",
        )
        .order("due_date", { ascending: true, nullsFirst: false });

      if (error) {
        setTasks([]);
        return;
      }

      setTasks(
        (data || []).map((row: any) => ({
          id: row.id,
          text: row.text || "",
          type: (row.type || "other") as TaskType,
          due_date: row.due_date,
          done_date: row.done_date,
          contact_id: row.contact_id,
          sales_id: row.sales_id,
          contact_name: row.contacts
            ? `${row.contacts.first_name || ""} ${row.contacts.last_name || ""}`.trim()
            : undefined,
          contact_company: row.contacts?.companies?.name,
        })),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const searchContacts = async (query: string) => {
    if (query.length < 2) {
      setContactSuggestions([]);
      return;
    }
    const { data } = await supabase
      .from("contacts")
      .select("id, first_name, last_name, companies(name)")
      .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
      .limit(6);
    setContactSuggestions(
      (data || []).map((c: any) => ({
        id: c.id,
        name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
        company: c.companies?.name || "",
      })),
    );
  };

  const handleComplete = async (task: Task) => {
    setCompletingId(task.id);
    const done_date = task.done_date ? null : new Date().toISOString();
    const { error } = await supabase
      .from("tasks")
      .update({ done_date })
      .eq("id", task.id);
    if (!error)
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, done_date } : t)),
      );
    setCompletingId(null);
  };

  const handleCreate = async () => {
    if (!form.text.trim()) return;
    setSaving(true);
    try {
      let contactId = form.contact_id;
      if (!contactId) {
        const { data: existing } = await supabase
          .from("contacts")
          .select("id")
          .eq("first_name", "General")
          .eq("last_name", "Tasks")
          .limit(1)
          .single();
        if (existing) {
          contactId = existing.id;
        } else {
          const { data: created } = await supabase
            .from("contacts")
            .insert({
              first_name: "General",
              last_name: "Tasks",
              tags: [],
              first_seen: new Date().toISOString(),
              last_seen: new Date().toISOString(),
              sales_id: identity?.id || null,
            })
            .select("id")
            .single();
          contactId = created?.id || null;
        }
      }
      const payload: any = {
        text: [form.text, form.notes].filter(Boolean).join("\n\n"),
        type: form.type,
        contact_id: contactId,
        sales_id: identity?.id || null,
      };
      if (form.due_date)
        payload.due_date = new Date(form.due_date).toISOString();
      const { error } = await supabase.from("tasks").insert(payload);
      if (!error) {
        await fetchTasks();
        setSheetOpen(false);
        setForm({
          text: "",
          type: "call",
          due_date: "",
          notes: "",
          contact_search: "",
          contact_id: null,
          contact_name: "",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const filtered = tasks.filter((t) => {
    if (filter === "open") return !t.done_date;
    if (filter === "done") return !!t.done_date;
    return true;
  });
  const openCount = tasks.filter((t) => !t.done_date).length;
  const overdueCount = tasks.filter(
    (t) => !t.done_date && isOverdue(t.due_date),
  ).length;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Follow-ups</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {openCount} open task{openCount !== 1 ? "s" : ""}
            {overdueCount > 0 && (
              <span className="ml-2 font-medium" style={{ color: "#FF4444" }}>
                · {overdueCount} overdue
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={() => setSheetOpen(true)}
          className="gap-2"
          style={{ background: "#3ECF8E", color: "#0F1629" }}
        >
          <Plus className="w-4 h-4" />
          New Task
        </Button>
      </div>

      <div className="flex gap-1 mb-4 bg-muted rounded-lg p-1 w-fit">
        {(["open", "done", "all"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              "px-4 py-1.5 rounded-md text-sm font-medium transition-all capitalize",
              filter === f
                ? "bg-background shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {f === "open"
              ? `Open (${openCount})`
              : f === "done"
                ? "Done"
                : "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <CheckCircle2 className="w-12 h-12 text-muted-foreground/30 mb-4" />
          <p className="text-lg font-medium text-muted-foreground">
            {filter === "done" ? "No completed tasks" : "No open tasks"}
          </p>
          <p className="text-sm text-muted-foreground/70 mt-1 mb-6">
            {filter === "open" && "Add a follow-up to get started."}
          </p>
          {filter === "open" && (
            <Button variant="outline" onClick={() => setSheetOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add your first task
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              onComplete={() => handleComplete(task)}
              completing={completingId === task.id}
            />
          ))}
        </div>
      )}

      {sheetOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-black/60"
            onClick={() => setSheetOpen(false)}
          />
          <div className="w-[420px] bg-background h-full flex flex-col shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b">
              <h2 className="text-lg font-semibold">New Task</h2>
              <button
                onClick={() => setSheetOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 px-6 py-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Task <span style={{ color: "#FF4444" }}>*</span>
                </label>
                <Input
                  placeholder="e.g. Call back — asked about pricing"
                  value={form.text}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, text: e.target.value }))
                  }
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Type</label>
                <div className="grid grid-cols-5 gap-1">
                  {(Object.keys(TYPE_CONFIG) as TaskType[]).map((t) => {
                    const config = TYPE_CONFIG[t];
                    const Icon = config.icon;
                    return (
                      <button
                        key={t}
                        onClick={() => setForm((p) => ({ ...p, type: t }))}
                        className={cn(
                          "flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-all",
                          form.type === t
                            ? "border-primary bg-primary/5 text-foreground"
                            : "border-border text-muted-foreground hover:border-primary/40",
                        )}
                      >
                        <Icon className={cn("w-4 h-4", config.color)} />
                        {config.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1.5 relative">
                <label className="text-sm font-medium flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  Contact (optional)
                </label>
                {form.contact_name ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-muted/50">
                    <span className="flex-1 text-sm font-medium">
                      {form.contact_name}
                    </span>
                    <button
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          contact_id: null,
                          contact_name: "",
                          contact_search: "",
                        }))
                      }
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Input
                      placeholder="Search leads or patients..."
                      value={form.contact_search}
                      onChange={(e) => {
                        setForm((p) => ({
                          ...p,
                          contact_search: e.target.value,
                        }));
                        searchContacts(e.target.value);
                      }}
                    />
                    {contactSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg z-10 overflow-hidden">
                        {contactSuggestions.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => {
                              setForm((p) => ({
                                ...p,
                                contact_id: c.id,
                                contact_name: c.name,
                                contact_search: "",
                              }));
                              setContactSuggestions([]);
                            }}
                            className="w-full text-left px-3 py-2.5 hover:bg-muted text-sm border-b last:border-b-0"
                          >
                            <div className="font-medium">{c.name}</div>
                            {c.company && (
                              <div className="text-xs text-muted-foreground">
                                {c.company}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Due date</label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, due_date: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  placeholder="Additional context..."
                  rows={3}
                  value={form.notes}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setSheetOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={handleCreate}
                disabled={!form.text.trim() || saving}
                style={
                  form.text.trim()
                    ? { background: "#3ECF8E", color: "#0F1629" }
                    : {}
                }
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                {saving ? "Saving…" : "Create Task"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TaskRow = ({
  task,
  onComplete,
  completing,
}: {
  task: Task;
  onComplete: () => void;
  completing: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const TypeIcon = TYPE_CONFIG[task.type]?.icon || Circle;
  const typeColor = TYPE_CONFIG[task.type]?.color || "text-slate-400";
  const overdue = !task.done_date && isOverdue(task.due_date);

  return (
    <div
      className={cn(
        "bg-card border rounded-xl transition-all",
        task.done_date ? "opacity-60" : "",
        overdue ? "" : "border-border",
      )}
      style={overdue ? { borderColor: "rgba(255,68,68,0.2)" } : undefined}
    >
      <div
        className="flex items-center gap-3 px-4 py-3.5 cursor-pointer"
        onClick={() => setExpanded((e) => !e)}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onComplete();
          }}
          className="flex-shrink-0 text-muted-foreground hover:text-emerald-400 transition-colors"
          disabled={completing}
        >
          {completing ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : task.done_date ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          ) : (
            <Circle className="w-5 h-5" />
          )}
        </button>
        <TypeIcon className={cn("w-4 h-4 flex-shrink-0", typeColor)} />
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "text-sm font-medium",
              task.done_date ? "line-through text-muted-foreground" : "",
            )}
          >
            {task.text.split("\n\n")[0]}
          </p>
          {task.contact_name && task.contact_name !== "General Tasks" && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {task.contact_name}
              {task.contact_company && ` · ${task.contact_company}`}
            </p>
          )}
        </div>
        {task.due_date && (
          <div
            className={cn(
              "flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
              task.done_date
                ? "text-muted-foreground"
                : overdue
                  ? ""
                  : "text-muted-foreground bg-muted",
            )}
            style={
              !task.done_date && overdue
                ? { color: "#FF4444", background: "rgba(255,68,68,0.12)" }
                : undefined
            }
          >
            <Clock className="w-3 h-3" />
            {formatDueDate(task.due_date)}
          </div>
        )}
        <ChevronDown
          className={cn(
            "w-4 h-4 text-muted-foreground transition-transform",
            expanded ? "rotate-180" : "",
          )}
        />
      </div>
      {expanded && task.text.includes("\n\n") && (
        <div className="px-12 pb-4 pt-1 border-t mx-4 mb-1">
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {task.text.split("\n\n").slice(1).join("\n\n")}
          </p>
        </div>
      )}
    </div>
  );
};
